import type { Tier } from '../theme/tokens';
import type { CarType } from '@parkproof/shared';

export type { CarType };

export type ParkingType = 'underground' | 'multi_storey' | 'surface';

/** Structural isolation per Q16: ingest writes osm.*, the verification trigger writes verified.*. */
export interface Dimensions {
  osm?: {
    heightM: { value: number; verifiedAt: number; confidence: number };
  };
  verified?: {
    heightM: { value: number; verifierCount: number; lastVerifiedAt: number };
  };
}

export interface Parking {
  id: string;
  name: string;
  address?: string;
  lat: number;
  lng: number;
  geohash: string;

  osmId?: string;
  countryCode?: string;
  ocrLanguages?: string[];

  dimensions: Dimensions;

  confidenceTier: Tier;
  testedCount: number;
  disputeCount: number;

  operator?: string;
  parkingType?: ParkingType;
  rawOsmTags?: Record<string, string>;

  /** Set true if a re-ingest confirms OSM removed this parking AND no verifications exist. */
  hidden?: boolean;
  /** Set false if OSM removed it but verifications exist (kept visible). */
  osmPresent?: boolean;

  createdAt: number;
  updatedAt: number;
}

/** The height limit to evaluate a car against — verified measurement wins over OSM's. */
export function bestHeightM(parking: Parking): number | undefined {
  return parking.dimensions.verified?.heightM.value ?? parking.dimensions.osm?.heightM.value;
}

/** Ingest lifecycle receipt — replaces the retired /cities collection. */
export interface IngestDoc {
  bbox: [number, number, number, number];
  status: 'pending' | 'ready' | 'failed';
  progressMessage?: string;
  parkingCount?: number;
  ingestedAt: number;
  requestedBy: string;
  countryCode: string;
  centerName?: string;
  error?: string | null;
}

export interface Car {
  id: string;
  label: string;
  type: CarType;
  heightM: number;
  hasRoofRack?: boolean;
  hasRoofBox?: boolean;
  hasTrailer?: boolean;
}

export interface User {
  id: string;
  email?: string;
  displayName?: string;
  cars: Car[];
  reputationScore: number;
  verificationsCount: number;
  homeCityId?: string;
  createdAt: number;
}

export type ReportType =
  | 'tested_ok'
  | 'reported_height'
  | 'reported_wrong'
  | 'photo_upload';

export interface ParkingReport {
  id: string;
  parkingId: string;
  userId: string;
  reportType: ReportType;
  reportedHeightM?: number;
  photoUrl?: string;
  ocrExtractedValue?: number;
  notes?: string;
  createdAt: number;
}

export interface ParkingFitInfo {
  fits: boolean;
  clearanceM: number;
  verdict: 'safe' | 'tight' | 'blocked' | 'unknown';
}

export const computeFit = (
  parking: Parking,
  carHeightM: number
): ParkingFitInfo => {
  const limit = bestHeightM(parking);
  if (limit == null) {
    return { fits: false, clearanceM: 0, verdict: 'unknown' };
  }
  const clearance = +(limit - carHeightM).toFixed(2);
  if (clearance < 0) return { fits: false, clearanceM: clearance, verdict: 'blocked' };
  if (clearance < 0.15) return { fits: true, clearanceM: clearance, verdict: 'tight' };
  return { fits: true, clearanceM: clearance, verdict: 'safe' };
};
